import React, { PropsWithChildren, useState, useCallback, useRef } from 'react'
import { Button, useModalContext } from '@sketch/components'
import {
  Footer,
  ModalStyled,
  ResizableContentWrapper,
  ResizeHandle,
  MODAL_MAX_HEIGHT_VH,
} from './MoveStorageItemModalLayout.styles'

export interface MoveStorageItemModalLayoutProps {}

export const MoveStorageItemModalLayout: React.FC<
  PropsWithChildren<MoveStorageItemModalLayoutProps>
> = props => {
  const hideModal = useModalContext().hideModal
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined)
  const [height, setHeight] = useState<number | undefined>(undefined)
  const footerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { children } = props

  const modalRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && !minHeight) {
        requestAnimationFrame(() => {
          const modalHeight = node.offsetHeight
          const footerHeight = footerRef.current?.offsetHeight ?? 0
          const calculatedHeight = modalHeight - footerHeight
          setMinHeight(calculatedHeight)
          setHeight(calculatedHeight)
        })
      }
    },
    [minHeight]
  )

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startY = e.clientY
      const startHeight = wrapperRef.current?.offsetHeight ?? height ?? 0
      const maxHeight = window.innerHeight * (MODAL_MAX_HEIGHT_VH / 100)

      document.body.style.cursor = 'row-resize'
      document.body.style.userSelect = 'none'

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientY - startY
        const newHeight = Math.min(
          maxHeight,
          Math.max(minHeight ?? 360, startHeight + delta)
        )
        setHeight(newHeight)
      }

      const handleMouseUp = () => {
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [height, minHeight]
  )

  const childArray = React.Children.toArray(children)
  const footer = childArray[childArray.length - 1]
  const content = childArray.slice(0, -1)

  return (
    <ModalStyled ref={modalRef} onCancel={hideModal}>
      <ResizableContentWrapper
        ref={wrapperRef}
        $minHeight={minHeight}
        style={height ? { height } : undefined}
      >
        {content}
        <ResizeHandle onMouseDown={handleResizeStart} />
      </ResizableContentWrapper>
      <div ref={footerRef}>{footer}</div>
    </ModalStyled>
  )
}

export interface MoveStorageItemModalFooterProps extends PropsWithChildren<{}> {
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  dimmed?: boolean
  actionLabel: string
  leftContent?: React.ReactElement
  /**
   * Whether to show ellipsis in the button text
   * @default false
   */
  indicateNextStep?: boolean
}
export const MoveStorageItemModalFooter: React.FC<
  PropsWithChildren<MoveStorageItemModalFooterProps>
> = props => {
  const hideModal = useModalContext().hideModal
  const {
    disabled,
    loading,
    onClick,
    dimmed,
    indicateNextStep,
    actionLabel,
    leftContent,
  } = props

  return (
    <Footer leftContent={leftContent} dimmed={dimmed}>
      <Button onClick={hideModal}>Cancel</Button>
      <Button
        variant="primary"
        disabled={disabled}
        loading={loading}
        onClick={onClick}
      >
        {actionLabel}
        {disabled || indicateNextStep ? '…' : ''}
      </Button>
    </Footer>
  )
}
